import { useEffect, useMemo, useRef, useState } from "react";
import { t, type Lang } from "../lib/i18n";

const FACES = "😀😁😂🤣😃😄😅😆😉😊😋😎😍😘😗😙😚🙂🤗🤩🤔🤨😐😑😶🙄😏😣😥😮🤐😯😪😫🥱😴😌😛😜😝🤤😒😓😔😕🙃🤑😲☹️🙁😖😞😟😤😢😭😦😧😨😩🤯😬😰😱🥵🥶😳🤪😵🥴😠😡🤬😷🤒🤕🤢🤮🤧😇🥳🥺🤠🤡🤥🤫🤭🧐🤓😈👿";
const HANDS = "👍👎👌✌️🤞🤟🤘🤙👋🤚🖐️✋🖖👏🙌🤲🤝🙏💪";
const MARKS = "❤️🧡💛💚💙💜🖤🤍💔⭐🌟✨🔥💯✅❌⚠️📌📎🔗💡📖📝📚🗂️📁📂🔒🔑🏷️";
const WORK = "🏠🚀🌙☀️🌈⚡❄️💧🌊🍀🌸🌻🍁🐶🐱🦊🐻🐼🐨🐯🦁🐮🐷🐸🐵🐔🐧🐦🦄🐢🐍🐙🦋🐝🐞";

function chars(raw: string): string[] {
  const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
  return [...segmenter.segment(raw)].map((part) => part.segment).filter((part) => part.trim());
}

const GROUPS: Array<{ id: string; ru: string; en: string; items: string[] }> = [
  { id: "faces", ru: "Лица", en: "Faces", items: chars(FACES) },
  { id: "hands", ru: "Жесты", en: "Hands", items: chars(HANDS) },
  { id: "marks", ru: "Знаки", en: "Marks", items: chars(MARKS) },
  { id: "work", ru: "Разное", en: "More", items: chars(WORK) },
];

type Props = {
  lang: Lang;
  top: number;
  left: number;
  onPick: (emoji: string) => void;
  onClose: () => void;
};

function recent(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem("hiera.emoji") || "[]") as string[];
    return raw.filter((item) => item).slice(0, 16);
  } catch {
    return [];
  }
}

export function EmojiPicker({ lang, top, left, onPick, onClose }: Props) {
  const [q, setQ] = useState("");
  const [used, setUsed] = useState(recent);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onPointer(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) onClose();
    }
    window.addEventListener("mousedown", onPointer);
    return () => window.removeEventListener("mousedown", onPointer);
  }, [onClose]);

  const query = q.trim();
  const shown = useMemo(() => {
    if (!query) return GROUPS;
    const hit = GROUPS.flatMap((group) => group.items).filter((item) => item.includes(query));
    return [{ id: "hit", ru: "Поиск", en: "Search", items: hit }];
  }, [query]);

  function choose(emoji: string) {
    const next = [emoji, ...used.filter((item) => item !== emoji)].slice(0, 16);
    localStorage.setItem("hiera.emoji", JSON.stringify(next));
    setUsed(next);
    onPick(emoji);
  }

  return (
    <div className="emoji-pop" ref={ref} style={{ top, left }}>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t(lang, "emojiSearch")}
        aria-label={t(lang, "emojiSearch")}
      />
      {!query && used.length ? (
        <section>
          <h3>{lang === "ru" ? "Частые" : "Frequent"}</h3>
          <div className="emoji-grid">
            {used.map((item) => (
              <button key={item} type="button" onClick={() => choose(item)}>{item}</button>
            ))}
          </div>
        </section>
      ) : null}
      {shown.map((group) => (
        <section key={group.id}>
          <h3>{lang === "ru" ? group.ru : group.en}</h3>
          <div className="emoji-grid">
            {group.items.map((item) => (
              <button key={group.id + item} type="button" onClick={() => choose(item)}>{item}</button>
            ))}
          </div>
        </section>
      ))}
      <button type="button" className="emoji-clear" onClick={() => onPick("")}>
        {t(lang, "clearEmoji")}
      </button>
    </div>
  );
}
